/**
 * KiotViet — schema payload, webhook mẫu, ánh xạ mặc định. Dựa theo Public API
 * (https://www.kiotviet.vn/huong-dan-su-dung-kiotviet/retail-ket-noi-api/public-api/):
 *  - Webhook gửi { Id, Attempt, Notifications: [{ Action, Data: [...] }] }, PascalCase.
 *  - Đơn hàng không kèm SĐT khách → luồng thật gọi GET /customers/{id}; demo ghép sẵn
 *    khối `Customer` và `CategoryName` từng dòng hàng như sau bước làm giàu.
 */
import type { Mapping, SampleEvent } from './types'

export const KIOTVIET_TOKEN_URL = 'https://id.kiotviet.vn/connect/token'
export const KIOTVIET_BASE_URL = 'https://public.kiotapi.com'
export const KIOTVIET_QUOTA = '5.000 GET / giờ'

/** Trường trong payload đơn hàng (đã làm giàu) — dùng cho bộ chọn trường. */
export interface SchemaField {
  path: string
  label: string
  type: 'string' | 'number' | 'date' | 'array' | 'object'
  /** Với mảng: trường con. */
  children?: SchemaField[]
}

export const KIOTVIET_ORDER_SCHEMA: SchemaField[] = [
  { path: 'Id', label: 'ID đơn', type: 'number' },
  { path: 'Code', label: 'Mã đơn', type: 'string' },
  { path: 'PurchaseDate', label: 'Ngày mua', type: 'date' },
  { path: 'BranchName', label: 'Chi nhánh', type: 'string' },
  { path: 'Status', label: 'Mã trạng thái', type: 'number' },
  { path: 'StatusValue', label: 'Trạng thái', type: 'string' },
  { path: 'Total', label: 'Tổng tiền', type: 'number' },
  { path: 'TotalPayment', label: 'Đã thanh toán', type: 'number' },
  { path: 'CustomerId', label: 'ID khách', type: 'number' },
  { path: 'CustomerCode', label: 'Mã khách', type: 'string' },
  { path: 'CustomerName', label: 'Tên khách', type: 'string' },
  {
    path: 'Customer', label: 'Khách (làm giàu từ GET /customers)', type: 'object',
    children: [
      { path: 'Customer.ContactNumber', label: 'SĐT khách', type: 'string' },
      { path: 'Customer.Gender', label: 'Giới tính', type: 'string' },
      { path: 'Customer.BirthDate', label: 'Ngày sinh', type: 'date' },
      { path: 'Customer.Groups', label: 'Nhóm khách', type: 'string' },
      { path: 'Customer.TotalInvoiced', label: 'Tổng đã mua', type: 'number' },
    ],
  },
  {
    path: 'OrderDetails[]', label: 'Dòng hàng', type: 'array',
    children: [
      { path: 'OrderDetails[].ProductId', label: 'ID sản phẩm', type: 'number' },
      { path: 'OrderDetails[].ProductCode', label: 'Mã sản phẩm', type: 'string' },
      { path: 'OrderDetails[].ProductName', label: 'Tên sản phẩm', type: 'string' },
      { path: 'OrderDetails[].CategoryName', label: 'Nhóm hàng (làm giàu từ GET /products)', type: 'string' },
      { path: 'OrderDetails[].Quantity', label: 'Số lượng', type: 'number' },
      { path: 'OrderDetails[].Price', label: 'Đơn giá', type: 'number' },
      { path: 'OrderDetails[].Discount', label: 'Giảm giá', type: 'number' },
    ],
  },
  { path: 'ModifiedDate', label: 'Sửa lần cuối', type: 'date' },
]

export const KIOTVIET_CUSTOMER_SCHEMA: SchemaField[] = [
  { path: 'Id', label: 'ID khách', type: 'number' },
  { path: 'Code', label: 'Mã khách', type: 'string' },
  { path: 'Name', label: 'Tên', type: 'string' },
  { path: 'ContactNumber', label: 'SĐT', type: 'string' },
  { path: 'Gender', label: 'Giới tính', type: 'string' },
  { path: 'BirthDate', label: 'Ngày sinh', type: 'date' },
  { path: 'Address', label: 'Địa chỉ', type: 'string' },
  { path: 'Groups', label: 'Nhóm khách', type: 'string' },
  { path: 'CreatedDate', label: 'Ngày tạo', type: 'date' },
]

/** Trường nội bộ (đích ánh xạ) — dùng cho điều kiện & biến trong tin nhắn. */
export const CRM_SCHEMA: SchemaField[] = [
  { path: 'customer.isFriend', label: 'Đã kết bạn Zalo (true/false)', type: 'string' },
  { path: 'customer.code', label: 'Mã khách', type: 'string' },
  { path: 'customer.name', label: 'Tên khách', type: 'string' },
  { path: 'customer.phone', label: 'SĐT khách (khoá ghép Zalo)', type: 'string' },
  { path: 'customer.gender', label: 'Giới tính', type: 'string' },
  { path: 'customer.group', label: 'Nhóm khách', type: 'string' },
  { path: 'order.code', label: 'Mã đơn', type: 'string' },
  { path: 'order.status', label: 'Trạng thái đơn', type: 'string' },
  { path: 'order.total', label: 'Tổng tiền', type: 'number' },
  { path: 'order.date', label: 'Ngày mua', type: 'date' },
  { path: 'order.branch', label: 'Chi nhánh', type: 'string' },
  {
    path: 'order.items[]', label: 'Sản phẩm trong đơn', type: 'array',
    children: [
      { path: 'order.items[].code', label: 'Mã SP', type: 'string' },
      { path: 'order.items[].name', label: 'Tên SP', type: 'string' },
      { path: 'order.items[].category', label: 'Nhóm hàng', type: 'string' },
      { path: 'order.items[].qty', label: 'Số lượng', type: 'number' },
      { path: 'order.items[].price', label: 'Đơn giá', type: 'number' },
    ],
  },
]

/** Ánh xạ mặc định KiotViet đơn hàng → CRM (sửa được trên UI). */
export const KIOTVIET_DEFAULT_MAPPING: Mapping = {
  id: 'map_kiotviet_order',
  source: 'kiotviet',
  name: 'KiotViet đơn hàng → CRM',
  rules: [
    { from: 'CustomerCode', to: 'customer.code', transform: 'trim' },
    { from: 'CustomerName', to: 'customer.name', transform: 'trim' },
    { from: 'Customer.ContactNumber', to: 'customer.phone', transform: 'phone_vn' },
    { from: 'Customer.Gender', to: 'customer.gender', transform: 'none' },
    { from: 'Customer.Groups', to: 'customer.group', transform: 'none' },
    { from: 'Code', to: 'order.code', transform: 'upper' },
    { from: 'StatusValue', to: 'order.status', transform: 'none' },
    { from: 'Total', to: 'order.total', transform: 'number' },
    { from: 'PurchaseDate', to: 'order.date', transform: 'date' },
    { from: 'BranchName', to: 'order.branch', transform: 'none' },
    { from: 'OrderDetails[].ProductCode', to: 'order.items[].code', transform: 'none' },
    { from: 'OrderDetails[].ProductName', to: 'order.items[].name', transform: 'trim' },
    { from: 'OrderDetails[].CategoryName', to: 'order.items[].category', transform: 'none' },
    { from: 'OrderDetails[].Quantity', to: 'order.items[].qty', transform: 'number' },
    { from: 'OrderDetails[].Price', to: 'order.items[].price', transform: 'number' },
  ],
}

/** Webhook mẫu nguyên bản KiotViet (đơn hàng), đã kèm khối làm giàu. */
export function kiotvietWebhookEnvelope(action: string, data: unknown[], attempt = 1) {
  return { Id: 'wh_' + Math.random().toString(36).slice(2, 8), Attempt: attempt, Notifications: [{ Action: action, Data: data }] }
}

const DON_KHANG_SINH = {
  Id: 1002931, Code: 'DH000527', PurchaseDate: '2026-09-22T09:12:00', BranchId: 1, BranchName: 'Nhà thuốc TDVN - Hoàng Ngân',
  Status: 3, StatusValue: 'Hoàn thành', Total: 385000, TotalPayment: 385000,
  CustomerId: 5581, CustomerCode: 'KH012004', CustomerName: 'Nguyễn Văn Hoàng',
  Customer: { ContactNumber: '0912 345 678', Gender: 'Nam', BirthDate: '1982-08-28', Groups: 'Hội viên Bạc', TotalInvoiced: 2549000 },
  OrderDetails: [
    { ProductId: 771, ProductCode: 'AMOX500', ProductName: 'Amoxicillin 500mg (hộp 100 viên)', CategoryName: 'Kháng sinh', Quantity: 1, Price: 185000, Discount: 0 },
    { ProductId: 812, ProductCode: 'PARA500', ProductName: 'Paracetamol 500mg', CategoryName: 'Giảm đau hạ sốt', Quantity: 2, Price: 25000, Discount: 0 },
    { ProductId: 903, ProductCode: 'VHT-200', ProductName: 'Vạn Hỷ Trà - Hộp thiếc 200g', CategoryName: 'Trà xanh', Quantity: 1, Price: 150000, Discount: 0 },
  ],
  ModifiedDate: '2026-09-22T09:12:31',
}

const DON_KHONG_KHANG_SINH = {
  ...DON_KHANG_SINH, Id: 1002944, Code: 'DH000531', PurchaseDate: '2026-09-22T10:40:00', Total: 235000, TotalPayment: 235000,
  CustomerId: 5590, CustomerCode: 'KH012088', CustomerName: 'Trần Thị Lan',
  Customer: { ContactNumber: '+84 987 654 321', Gender: 'Nữ', BirthDate: '1990-02-14', Groups: 'Hội viên', TotalInvoiced: 640000 },
  OrderDetails: [
    { ProductId: 903, ProductCode: 'VHT-200', ProductName: 'Vạn Hỷ Trà - Hộp thiếc 200g', CategoryName: 'Trà xanh', Quantity: 1, Price: 150000, Discount: 0 },
    { ProductId: 640, ProductCode: 'VITC1000', ProductName: 'Vitamin C 1000mg', CategoryName: 'Vitamin', Quantity: 1, Price: 85000, Discount: 0 },
  ],
  ModifiedDate: '2026-09-22T10:40:12',
}

const KHACH_MOI = {
  Id: 5602, Code: 'KH012101', Name: 'Phạm Minh Đức', ContactNumber: '0369 888 777', Gender: 'Nam', BirthDate: '1995-11-03',
  Address: 'Số 5 Hoàng Văn Thụ, TP Thái Nguyên', Groups: '', CreatedDate: '2026-09-22T11:05:00',
}

export const KIOTVIET_SAMPLES: SampleEvent[] = [
  { id: 'kv_order_khang_sinh', source: 'kiotviet', event: 'order.update', label: 'Đơn DH000527 — có Amoxicillin (kháng sinh)', payload: kiotvietWebhookEnvelope('order.update', [DON_KHANG_SINH]) },
  { id: 'kv_order_thuong', source: 'kiotviet', event: 'order.update', label: 'Đơn DH000531 — chỉ trà & vitamin', payload: kiotvietWebhookEnvelope('order.update', [DON_KHONG_KHANG_SINH]) },
  { id: 'kv_customer_moi', source: 'kiotviet', event: 'customer.update', label: 'Khách mới KH012101 — Phạm Minh Đức', payload: kiotvietWebhookEnvelope('customer.update', [KHACH_MOI]) },
]

export const GSHEET_SAMPLES: SampleEvent[] = [
  {
    id: 'gs_row_khach', source: 'gsheet', event: 'row.added', label: 'Sheet "Khách quan tâm" — dòng mới',
    payload: { row: 128, values: { 'Họ tên': 'Lê Thu Hà', 'SĐT': '0905 111 222', 'Nhu cầu': 'Trà biếu Tết', 'Nguồn': 'Facebook Ads' } },
  },
]

/** Bóc từng Data[i] khỏi phong bì webhook KiotViet → mỗi bản ghi một sự kiện. */
export function tachBanGhiKiotViet(payload: unknown): Array<{ action: string; data: Record<string, unknown> }> {
  const p = payload as { Notifications?: Array<{ Action: string; Data: unknown[] }> }
  const ra: Array<{ action: string; data: Record<string, unknown> }> = []
  for (const n of p?.Notifications ?? []) for (const d of n.Data ?? []) ra.push({ action: n.Action, data: d as Record<string, unknown> })
  return ra
}
